/**
 * Acknowledging one policy.
 *
 * This page produces the record the product is bought for: evidence that a
 * named person, on a named date, confirmed they had read a specific version of
 * a specific document. A surveyor may eventually be looking at it.
 *
 * So it is deliberately not one button. The reader has to open the document
 * and then tick a statement that says what they are attesting to, because an
 * attestation nobody could have read is worth nothing — and a hospital that
 * relied on it would find that out at the worst possible moment.
 *
 * IT IS BOUND TO ONE VERSION, THE REQUIREMENT'S. Finding R1: the page used to
 * take its title, its summary and its "Open the document" link from whatever
 * version happened to be current when the page loaded, while submitting the
 * version id the requirement named. In ordinary use those agree. When they do
 * not — because a newer version published, or because supersession half failed
 * — the page showed one document over an attestation to another. Everything
 * here now comes from `record.policyVersionId`, the version label is on the
 * screen, and the document link carries it.
 *
 * The server gate is still the authority. `ackGate` refuses a version that is
 * not the one in force and a requirement that is not actionable, whatever this
 * page believes; this is what a reader sees, not what protects the record.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BackLink } from '../../shared/BackLink';
import { useAuth } from '../../auth/useAuth';
import { usePolicy } from '../policies/usePolicy';
import { usePolicyVersion } from '../policies/useVersions';
import { closedBecause, isOutstanding, useAcknowledge, useMyAcknowledgements } from './useAcknowledgements';
import { isCallFailure } from '../../lib/callable';
import { formatDate } from '../policies/policyTable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import styles from '../../shared/Page.module.css';
import own from './AcknowledgePage.module.css';

export function AcknowledgePage() {
  const { ackId, policyId } = useParams<{ ackId: string; policyId: string }>();
  const { session } = useAuth();

  const policy = usePolicy(session?.orgId ?? null, policyId);
  const acks = useMyAcknowledgements(session?.orgId ?? null, session?.uid ?? null);
  const acknowledge = useAcknowledge(session?.orgId ?? null, session?.uid ?? null);

  const record = acks.data?.find((a) => a.id === ackId);
  // The version this requirement names, not the one in force today. They are
  // the same in ordinary use and the difference is the whole point of R1.
  const version = usePolicyVersion(
    session?.orgId ?? null,
    policyId,
    record?.policyVersionId ?? null,
  );

  const [confirmed, setConfirmed] = useState(false);

  if (policy.isLoading || acks.isLoading) return <Spinner label="Loading policy" />;

  if (!policy.data) {
    return (
      <PageState title="Policy not found" action={{ to: '/', label: 'Back to your tasks' }}>
        The policy this acknowledgement refers to could not be opened. It may
        have been retired since you were asked to read it.
      </PageState>
    );
  }

  /**
   * A REQUEST THAT IS NOT THIS PERSON'S IS NOT A TASK.
   *
   * The list is constrained to `userId == me` by the rules, so an id belonging
   * to somebody else simply is not in it. Possessing the URL from a forwarded
   * email therefore shows this, and grants nothing.
   */
  if (!record) {
    return (
      <PageState title="This task is not yours" action={{ to: '/', label: 'Back to your tasks' }}>
        No acknowledgement with that address is waiting for you. If somebody
        sent you this link, they were asked to read it, not you.
      </PageState>
    );
  }

  if (record.status === 'acknowledged' || record.status === 'waived' || acknowledge.isSuccess) {
    return (
      <div className={own.wrap}>
        <div className={own.done}>
          <div className={own.doneMark} aria-hidden="true">&#10003;</div>
          {/* role=status so the change is announced: the form this replaced
              has just disappeared from under the person who submitted it. */}
          <div role="status">
            <h1 className={styles.stateTitle}>{record.status === 'waived' ? 'Waived' : 'Acknowledged'}</h1>
            <p className={styles.stateBody}>
              {record.status === 'waived' ? (
                <>An administrator excused you from <strong>{policy.data.title}</strong>, so there is nothing to do.</>
              ) : (
                <>
                  Your acknowledgement of <strong>{policy.data.title}</strong>
                  {record.versionNumber != null && <> version {record.versionNumber}</>} has
                  been recorded
                  {record.acknowledgedAt && ` on ${formatDate(record.acknowledgedAt)}`}.
                  It will appear in your organization&rsquo;s compliance record.
                </>
              )}
            </p>
          </div>
          <Link to="/" className={`${styles.btn} ${styles.btnPrimary}`}>
            Back to your tasks
          </Link>
        </div>
      </div>
    );
  }

  /**
   * CLOSED WITHOUT ANYBODY READING ANYTHING. Cancelled, or a status this build
   * has no name for. Either way the server will refuse an attestation, so no
   * attestation is offered. Finding R4's half of this page.
   */
  if (!isOutstanding(record)) {
    return (
      <PageState title="No longer required" action={{ to: '/', label: 'Back to your tasks' }}>
        {record.status === 'cancelled' ? (
          <>
            <strong>{policy.data.title}</strong>
            {record.versionNumber != null && <> version {record.versionNumber}</>} is
            no longer waiting for you. {closedBecause(record.cancelledReason)} If a newer
            version needs you, it will be on your tasks.
          </>
        ) : (
          <>
            StandingDocs cannot tell what state this requirement is in, so it is not
            offering you an attestation. Ask an administrator to look at it.
          </>
        )}
      </PageState>
    );
  }

  /**
   * A REQUIREMENT THAT NAMES NO VERSION CANNOT BE ATTESTED TO.
   *
   * Nothing writes one today, and an attestation that does not say which
   * version it is about proves nothing to the hospital relying on it, so the
   * page says so rather than sending a request the server would refuse.
   */
  if (!record.policyVersionId) {
    return (
      <PageState title="This request is incomplete" action={{ to: '/', label: 'Back to your tasks' }}>
        This acknowledgement request could not be matched to a policy version, so it
        cannot be recorded. Ask an administrator to reissue it.
      </PageState>
    );
  }

  /**
   * THE REQUIREMENT NAMES A VERSION THAT IS NO LONGER IN FORCE.
   *
   * Only reachable when supersession failed part way, which publication
   * tolerates deliberately rather than un-publishing a live document. The
   * server refuses this, and so does the page, rather than quietly attesting
   * to a version nobody is being asked to read.
   */
  const stale = policy.data.currentVersionId != null
    && record.policyVersionId !== policy.data.currentVersionId;
  if (stale) {
    return (
      <PageState title="This version has been replaced" action={{ to: `/policy/${policy.data.id}`, label: 'Open the policy' }}>
        You were asked to read version {record.versionNumber ?? '?'} of{' '}
        <strong>{policy.data.title}</strong>, and a newer version has been published since.
        This one cannot be acknowledged. Open the policy to read the version in force.
      </PageState>
    );
  }

  const failure = acknowledge.error;
  const versionLabel = record.versionNumber != null ? `Version ${record.versionNumber}` : 'This version';
  const effective = version.data?.effectiveDate ?? null;

  return (
    <div className={own.wrap}>
      <div className={styles.titled}>
        <p className={styles.eyebrow}>{policy.data.policyNumber}</p>
        <div className={styles.titleLine}>
          <BackLink to="/" label="Back to your tasks" />
          <h1 className={styles.title}>{policy.data.title}</h1>
        </div>
        {/* THE VERSION, ON THE SCREEN. The one fact the page never said. */}
        <p className={styles.subtitle}>
          <strong>{versionLabel} — acknowledgement required</strong>
          {effective && <> · effective {formatDate(effective)}</>}
          {record.dueDate && <> · due {formatDate(record.dueDate)}</>}
        </p>
        {policy.data.summary && <p className={styles.subtitle}>{policy.data.summary}</p>}
      </div>

      {failure && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert" style={{ marginTop: 'var(--sp-5)' }}>
          {isCallFailure(failure)
            ? failure.message
            : 'Your acknowledgement could not be recorded. Please try again.'}
        </div>
      )}

      <div className={own.statement}>
        <h2 className={own.statementTitle}>Before you confirm</h2>
        <p>
          Open the document and read it. Acknowledging records that you have
          done so, against {versionLabel.toLowerCase()}, with today&rsquo;s date and your name.
        </p>
        <p>
          {/* The SAME version the attestation binds to, named in the address. */}
          <Link
            to={`/policy/${policy.data.id}/view?version=${encodeURIComponent(record.policyVersionId)}`}
            className={`${styles.btn}`}
          >
            Open the document
          </Link>
        </p>
      </div>

      {/* A real label wrapping a real checkbox: clicking the text toggles it,
          and a screen reader reads the sentence as the control's name. */}
      <label className={own.confirm}>
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <span className={own.confirmText}>
          I have read and understood <strong>{policy.data.title}</strong>
          {record.versionNumber != null && <>, version {record.versionNumber}</>}, and I
          will follow it in my work.
        </span>
      </label>

      <div className={own.actions}>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          disabled={!confirmed || acknowledge.isPending}
          onClick={() =>
            acknowledge.mutate({
              policyId: policy.data!.id,
              policyVersionId: record.policyVersionId,
            })
          }
        >
          {acknowledge.isPending ? 'Recording…' : 'Acknowledge'}
        </button>
        <Link to="/" className={styles.btn}>
          Not now
        </Link>
      </div>
    </div>
  );
}
